import { IWebXRActionType, IXRInteractionHandler, IXRInteractionType, useWebXRStore } from "../../../zustand/webxr-store"
import { useRef, useCallback, useLayoutEffect, useEffect, RefObject} from 'react'
import { Group, Intersection, Matrix4, Object3D, Object3DEventMap, WebGLRenderer, XRGripSpace, XRTargetRaySpace } from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import { useSceneStore } from "../../../zustand";
import { getControllerRayIntersections, sortIntersectionsByRenderOrder, transformCursorByIntersection } from "./webxr-utils";
import { useRefState } from "../../../hooks";
import { useGetCurrentSceneId } from "../../../hooks/general";

// This hook is used in component hoc's to store the their interaction handlers in the global zustand webxr store.
// These get then called for controller select events in the webxr tracking component
// based on https://github.com/pmndrs/react-xr/blob/master/src/XREvents.ts#L27
export function useInteraction(_target: ( React.RefObject<Group<Object3DEventMap> | null>) | string, eventType: IXRInteractionType, handler: IXRInteractionHandler, enabled: boolean = true) {
    const { scene } = useThree();
    const webxrDispatch = useWebXRStore(({ dispatch }) => dispatch);
    const handlerRef = useCallbackRef(handler);
  
    useEffect(() => {
        if (!enabled) return;
        let target = typeof _target === 'string' ? scene.getObjectByName(`hit-test-group-wrapper-${_target}`)?.children[0] as Object3D<Object3DEventMap> | undefined : _target.current; 
        if (!target || !handlerRef.current) return;

        const payload = {target, eventType, handlerRef: handlerRef as React.RefObject<IXRInteractionHandler>}
        webxrDispatch({type: IWebXRActionType.ADD_INTERACTION, payload}); 
    }, [_target, eventType, webxrDispatch, enabled])
}

/**
 * Returns a mutable callback function for event handlers.
 */
export function useCallbackRef<T>(fn: T): RefObject<T> {
  const ref = useRef<T>(fn)
  useLayoutEffect(() => void (ref.current = fn), [fn])
  return ref
}


const tempMatrix = new Matrix4()

// Hook based on https://github.com/pmndrs/react-xr/blob/master/src/Interactions.tsx#L42
export const useGetControllerRayIntersections = (index: number, space: 'grip' | 'target' , gl: WebGLRenderer) => {
    const interactions = useWebXRStore((state) => state.interactions);
    const raycaster = useThree((state) => state.raycaster);
    raycaster.firstHitOnly = true;

    return useCallback(
        () => {
            const getControlleGrouprMethod = space === 'grip' ? 'getControllerGrip' : 'getController';
            const controllerGroup = gl.xr[getControlleGrouprMethod](index);

            const objects = Array.from(interactions.keys());
            tempMatrix.identity().extractRotation(controllerGroup.matrixWorld)
            raycaster.ray.origin.setFromMatrixPosition(controllerGroup.matrixWorld)
            raycaster.ray.direction.set(0, 0, -1).applyMatrix4(tempMatrix)
            return [raycaster.intersectObjects(objects, true), controllerGroup] as [Intersection[], XRGripSpace | XRTargetRaySpace]
        },[interactions, raycaster]
    )
}

export const useControllerIntersections = (
    cursorRef: React.RefObject<Object3D | null>, 
    controllerGroup: Group
) => {

    const [hasIntersection, setHasIntersection, hasIntersectionRef] = useRefState(false);
    const {raycaster} = useThree();
    raycaster.firstHitOnly = true;
    const currentSceneId = useGetCurrentSceneId();

    useFrame(() => {
        if (!controllerGroup) return;
        const store = useWebXRStore.getState();
        if (!store) return;
        let intersections = getControllerRayIntersections(Array.from(store.interactions.keys()), raycaster, controllerGroup, currentSceneId);
        if (useSceneStore.getState().placementMode && hasIntersectionRef.current) {
            setHasIntersection(false);
            return;
        }

        if (!!intersections?.length) {
            if (!cursorRef.current) return;
            const nearest = intersections.sort(sortIntersectionsByRenderOrder)[0] // TODO: check if this is correct;
            transformCursorByIntersection(cursorRef.current, nearest);
        }

        if (hasIntersectionRef.current !== !!intersections?.length && !useSceneStore.getState().placementMode) {
            setHasIntersection(!!intersections?.length)
        }
    })
    return hasIntersection;
}
